import { Component, Input, OnDestroy, OnInit , signal } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable, Subscription, catchError, forkJoin, lastValueFrom, map } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { StorageKeys, SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Utilities } from 'src/app/core/services/utilities';
import { Perforation } from 'src/app/perical/models/perforation.model';
import { ThermalOperationsService } from 'src/app/perical/services/thermal-operations.service';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { UdtService } from 'src/app/shared/services/udt.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { WellConfigService } from 'src/app/shared/services/well-config.service';

@Component({
    selector: 'app-perforations',
    templateUrl: './perforations.component.html',
    styles: [``],
    providers: [DialogService, ConfirmationService],
    standalone: false
})
export class PerforationsComponent implements OnInit, OnDestroy {

  public columnDefs: any[];
  public tableHeight: string = '600px';

  private _perforations: Perforation[]
  public selectedPerforation: Perforation;
  public perforationsForm: UntypedFormGroup;
  public perforationFg: UntypedFormGroup;
  private _designId: string;
  private _subscriptions: Subscription = new Subscription();
  private _perfsOrder: string[] = [];

  get perforations(): UntypedFormArray {
    return this.perforationsForm.get("perforations") as UntypedFormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  // Validation
  public perforationDefaultDepth: number;
  public isLoading = signal<boolean>(false);
  public configMissing: boolean;
    public errorMessage: string;

  // State
  @Input()
  public componentId: string;

  constructor(
    private _messenger: MediatorService,
    private _thermalOperationsService: ThermalOperationsService,
    private _formBuilder: UntypedFormBuilder,
    private _wellConfigService: WellConfigService,
    private _store: StoreService,
    private _confirmationService: ConfirmationService,
    private _signalRService: SignalRService,
    private _udtService: UdtService,
    private _trajectoryService: TrajectoryService
  ) {
    this.isLoading.set(true);

    this.perforationsForm = this._formBuilder.group({
      perforations: this._formBuilder.array([]),
    });
   }

  async ngOnInit(): Promise<void> {
    this._designId = this._store.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS).designId;
    const uu = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);

    this.columnDefs = [
      { field: 'name', header: 'Name' },
      { field: 'measuredDepth', header: `Depth (${uu.longLengths} MD)` },
      { field: 'temperature', header: `UDT (${uu.temperature})` }
    ];

    this.getDataAndDefaults(false);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
  }

  signalRfunc(data: { action: string, designId: string }) {
    if (data.action == PeriforOnChangeMessages.REFRESH_UDT_PLOT || data.action === PeriforOnChangeMessages.REFRESH_TUBULARS_LIST) {
      this.getDataAndDefaults(true);
    }
  }

  private getDataAndDefaults(isSignalR: boolean) {
    const sources: Observable<any>[] = [
      this._wellConfigService.getTubulars() as Observable<any>,
      this._udtService.getUndisturbedTemperaturePoints() as Observable<any>
    ];

    forkJoin(sources).pipe(
      map(async ([tubulars, udtPoints]) => {
      if (tubulars.length > 0) {
        const udtDepths = udtPoints.map((p: { trueVerticalDepth: number; }) => p.trueVerticalDepth);
        this.perforationDefaultDepth = Math.max(...tubulars.map(x => x.shoeMd));
        const deepestShoeTvd = await lastValueFrom(this._trajectoryService.getTvdsFromMds([this.perforationDefaultDepth]));
        if (udtPoints.length > 0 && deepestShoeTvd[0] - 0.01 > Math.max(...udtDepths)) {
          this.errorMessage = "UDT Profile must be specified at least down to the deepest shoe";
          this.configMissing = true;
          this.isLoading.set(false);
          return;
        }
        this.perforationFg?.controls.measuredDepth.setValidators(Validators.max(this.perforationDefaultDepth));

        this._thermalOperationsService.getPerforations().subscribe(perforations => {
          this._perforations = perforations;
          this.populateFormData(isSignalR);
          this._perfsOrder = this._perforations.map(p => p.id);
        });
      } else {
        this.errorMessage = "Well Configuration Missing: Please specify at least one string";
        this.configMissing = true;
        this.isLoading.set(false);
      }
      }),
      catchError(err => {
        this.isLoading.set(false);
        this.configMissing = false
        return err;
      })).subscribe();
  }

  private populateFormData(isSignalR: boolean) {
    this._perforations.forEach((data, idx) => {
      const perforationFg = this.newPerforation(false);
      if (!isSignalR) {
        this.perforations.push(perforationFg);
      }
      this.perforations.controls[idx].setValue(data, { emitEvent: false });
    });
    this.isLoading.set(false);
  }

  private newPerforation(isNewPerforation: boolean): UntypedFormGroup {
    this.perforationFg = new UntypedFormGroup({
      id: new UntypedFormControl(Utilities.generateFakeGuid()), // Setting a unique id here so that prime-ng p-table has a data key for the expand and collapse functionality.
      designId : new UntypedFormControl(this._designId),
      name: new UntypedFormControl(null, { updateOn: 'blur', validators:  Validators.required }),
      measuredDepth: new UntypedFormControl(this.perforationDefaultDepth, { updateOn: 'blur', validators: [Validators.required, Validators.max(this.perforationDefaultDepth)] }),
      temperature: new UntypedFormControl({value: null, disabled: true})
    });

    this._subscriptions.add(this.perforationFg.valueChanges.subscribe((v) => this.handleSavePerforationRow(v, this.perforationFg)));

    if (isNewPerforation) {
      this.perforationFg.controls.measuredDepth.patchValue(this.perforationFg.controls.measuredDepth.value);
    }

    this.perforationFg.markAllAsTouched();

    return this.perforationFg;
  }

  private async handleSavePerforationRow(v: any, perforationRecord: UntypedFormGroup): Promise<void> {
    if (perforationRecord.valid && !this.isLoading() && v.measuredDepth <= this.perforationDefaultDepth) {
      if (v.id.length == 24) { // it's only on the client so save to db as a new record
        this.isLoading.set(true);
        await lastValueFrom(this._thermalOperationsService.setPerforations(new Perforation(v))).then(() => {
          this._perfsOrder.push(v.id);
          this.populateIdandTemperature();
          this._messenger.publish(new DataSavedMessage('Perforations'));
          this.isLoading.set(false);
        });
      } else {
        v.designId = this._designId;
        await lastValueFrom(this._thermalOperationsService.updatePerforation(new Perforation(v))).then(() => {
          this._perfsOrder = this._perfsOrder.filter(id => id !== v.id);
          this._perfsOrder.push(v.id);
          this.populateIdandTemperature();
          this._messenger.publish(new DataSavedMessage('Perforations'));
        });
      }
    }
  }

  private async populateIdandTemperature(): Promise<void> {
    await lastValueFrom(this._thermalOperationsService.getPerforations()).then(perfs => {
      this.clearFormArray(this.perforations);
      this._perforations = perfs;

      this._perforations.sort((a, b) => {
        const indexA = this._perfsOrder.indexOf(a.id);
        const indexB = this._perfsOrder.indexOf(b.id);
        return indexA - indexB;
      });

      this.populateFormData(false);
    });
  }

  public onAddPerforation(idx?: number): void {
    if (this.perforations.invalid) {
      return;
    }
    this.perforations.insert(idx || 0, this.newPerforation(true));
  }

  public onDeletePerforation(idx: number): void {
    const perforationId = this.perforations.controls[idx].value.id;
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete the selected perforation?',
      accept: async () => {
        if (perforationId.length > 24) { // it's been saved to the db, so delete through API
            await lastValueFrom(this._thermalOperationsService.deletePerforation(perforationId));
            this._messenger.publish(new DataSavedMessage('Perforations'));
        } else {
          this._perforations = this._perforations.filter(val => val.id !== perforationId);
        }

        this.perforations.removeAt(idx);
        }
    });
  }

  clearFormArray = (formArray: UntypedFormArray) => {
    while (formArray.length !== 0) {
      formArray.removeAt(0)
    }
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
    this.signalRfunc = null;
  }
}
